import { FC, useState, useMemo } from "react";
import { Spin, Empty, Input, Button, Checkbox, Table, Tag, Tooltip, Tabs, Badge, Modal, message, Segmented } from "antd";
import { Search, Users, X, Plus, ChevronRight, Clock, UserCheck, UserX, Calendar, HelpCircle, Zap, Mail, AlertCircle, ThumbsUp, Check, LayoutList, Columns3 } from "lucide-react";
import { DailyStaffView } from "./daily-staff-view";
import { useGetBookings } from "~/services/booking";
import { useUpdateServiceTicket } from "~/services/service-ticket/service-ticket.mutations";
import { useGetAllStaff } from "~/services/operation";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { useGetBatSignalResponses } from "~/services/communication";
import { TServiceTicket, TStaff } from "~/types";
import { formatFullAddress, capitalize } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { useNotification } from "~/hooks";
import { StaffAvailability } from "~/utils/constants";
import { baseService } from "~/services/base";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";

interface MasterStaffingProps {
  projectId: string;
}

interface StaffWithAvailability {
  staff: TStaff;
  availability: StaffAvailability | null;
  hasAvailabilityRecord: boolean;
  bookedHours: number;
  availableHours: number;
  note?: string;
  isScheduledToday: boolean;
}

interface PositionStaffAssignment {
  positionId: string;
  positionName: string;
  quantity: number;
  startTime: string;
  duration: number;
  hourlyRate: number;
  assignedStaffIds: string[];
}

type AvailabilityTab = 'all' | 'available' | 'scheduled' | 'unavailable' | 'pending';
type StaffingMode = 'general' | 'position';

type ViewMode = 'services' | 'daily-staff';

export const MasterStaffing: FC<MasterStaffingProps> = ({ projectId }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('services');
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<AvailabilityTab>('all');
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [staffingMode, setStaffingMode] = useState<StaffingMode>('general');
  const [positionAssignments, setPositionAssignments] = useState<Map<string, string[]>>(new Map());
  const [batSignalModalOpen, setBatSignalModalOpen] = useState(false);
  const [batSignalPosition, setBatSignalPosition] = useState<{ id: string; name: string } | null>(null);
  const [sendingBatSignal, setSendingBatSignal] = useState(false);
  const [batSignalPreview, setBatSignalPreview] = useState<{
    positionName: string;
    staffCount: number;
    staff: Array<{ id: string; name: string; email: string; role?: string }>;
  } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedBatSignalStaff, setSelectedBatSignalStaff] = useState<Set<string>>(new Set());
  const { success, error } = useNotification();

  // Fetch bookings for the project
  const { data: bookingsData, isLoading: bookingsLoading } = useGetBookings({
    projectId: projectId,
    pageSize: 500,
  });

  // Fetch all staff for assignment
  const { data: staffData, isLoading: staffLoading } = useGetAllStaff({
    getAll: true,
    pageSize: 100,
  });

  // Get the selected service's date for staff availability
  const selectedDate = selectedService
    ? dayjs(selectedService.serviceDatetime || selectedService.startDatetime)
    : null;

  const startOfDay = selectedDate?.startOf('day').toISOString();
  const endOfDay = selectedDate?.endOf('day').toISOString();

  // Fetch staff availability for the selected date
  const { data: availabilityData, isLoading: availabilityLoading } = useGetStaffAvailabilities(
    {
      startDate: startOfDay,
      endDate: endOfDay,
      pageSize: 200,
    },
    !!selectedDate
  );

  // Fetch bat signal responses for the selected service
  const { data: batSignalResponses, isLoading: responsesLoading, refetch: refetchResponses } = useGetBatSignalResponses(
    selectedService?.id,
    !!selectedService
  );

  // Get staff who have accepted (interested)
  const interestedStaff = useMemo(() => {
    if (!batSignalResponses?.responses) return [];
    return batSignalResponses.responses.filter(r => r.status === 'ACCEPTED');
  }, [batSignalResponses?.responses]);

  // Update service ticket mutation
  const { mutate: updateServiceTicket, isPending: isUpdating } = useUpdateServiceTicket();

  // Extract and flatten all service tickets from bookings
  const projectServices = useMemo(() => {
    if (!bookingsData?.items) return [];

    const allServices: TServiceTicket[] = [];
    bookingsData.items.forEach(booking => {
      if (booking.serviceTicket && booking.serviceTicket.length > 0) {
        booking.serviceTicket.forEach(service => {
          allServices.push({
            ...service,
            jobTicket: booking,
          });
        });
      }
    });

    return allServices.sort((a, b) => {
      const dateA = dayjs(a.serviceDatetime || a.startDatetime);
      const dateB = dayjs(b.serviceDatetime || b.startDatetime);
      return dateA.isBefore(dateB) ? -1 : 1;
    });
  }, [bookingsData?.items]);

  // Build staff list with availability info
  const staffWithAvailability = useMemo((): StaffWithAvailability[] => {
    if (!staffData?.items) return [];

    const availabilityMap = new Map<string, {
      availability: StaffAvailability;
      bookedHours: number;
      availableHours: number;
      note?: string;
    }>();

    if (availabilityData?.items) {
      availabilityData.items.forEach(schedule => {
        if (schedule.staffId) {
          availabilityMap.set(schedule.staffId, {
            availability: schedule.availability || StaffAvailability.FULL_TIME,
            bookedHours: schedule.bookedHours || 0,
            availableHours: schedule.availableHours || 8,
            note: schedule.note,
          });
        }
      });
    }

    return staffData.items.map(staff => {
      const availInfo = availabilityMap.get(staff.id);
      const hasRecord = availInfo !== undefined;

      return {
        staff,
        availability: hasRecord ? availInfo.availability : null,
        hasAvailabilityRecord: hasRecord,
        bookedHours: availInfo?.bookedHours || 0,
        availableHours: hasRecord ? (availInfo.availableHours || 8) : 0,
        note: availInfo?.note,
        isScheduledToday: (availInfo?.bookedHours || 0) > 0,
      };
    });
  }, [staffData?.items, availabilityData?.items]);

  // Check if selected service is an expo service
  const isExpoService = selectedService?.isExpoDay || (selectedService?.positions && selectedService.positions.length > 0);

  // Get positions from the expo service
  const expoPositions = useMemo(() => {
    if (!isExpoService || !selectedService?.positions) return [];
    return selectedService.positions;
  }, [isExpoService, selectedService?.positions]);

  // Filter staff based on search, tab, and position
  const filteredStaff = useMemo(() => {
    let filtered = staffWithAvailability;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(({ staff }) => {
        const name = formatStaffName(staff).toLowerCase();
        return name.includes(query);
      });
    }

    if (activeTab !== 'all') {
      filtered = filtered.filter(({ availability, hasAvailabilityRecord, isScheduledToday }) => {
        switch (activeTab) {
          case 'available':
            return hasAvailabilityRecord &&
                   (availability === StaffAvailability.FULL_TIME || availability === StaffAvailability.PART_TIME) &&
                   !isScheduledToday;
          case 'scheduled':
            return isScheduledToday;
          case 'unavailable':
            return hasAvailabilityRecord && availability === StaffAvailability.UNAVAILABLE;
          case 'pending':
            return !hasAvailabilityRecord;
          default:
            return true;
        }
      });
    }

    // Filter by position capability
    if (selectedPositionId) {
      filtered = filtered.filter(({ staff }) => {
        // Check if staff has this position in their positions array
        const hasPosition = staff.positions?.some(pos => pos.id === selectedPositionId);
        // Also check staffRole
        const hasRole = staff.staffRole?.id === selectedPositionId;
        return hasPosition || hasRole;
      });
    }

    return filtered;
  }, [staffWithAvailability, searchQuery, activeTab, selectedPositionId]);

  // Count by availability category
  const availabilityCounts = useMemo(() => {
    const counts = { all: 0, available: 0, scheduled: 0, unavailable: 0, pending: 0 };
    staffWithAvailability.forEach(({ availability, hasAvailabilityRecord, isScheduledToday }) => {
      counts.all++;

      if (!hasAvailabilityRecord) {
        counts.pending++;
      } else if (availability === StaffAvailability.UNAVAILABLE) {
        counts.unavailable++;
      } else if (isScheduledToday) {
        counts.scheduled++;
      } else if (availability === StaffAvailability.FULL_TIME || availability === StaffAvailability.PART_TIME) {
        counts.available++;
      }
    });
    return counts;
  }, [staffWithAvailability]);

  // When selecting a service, initialize the selected staff
  const handleSelectService = (service: TServiceTicket) => {
    setSelectedService(service);
    setSelectedStaffIds(service.staffIds || service.staffs?.map(s => s.id) || []);
    setSearchQuery("");
    setActiveTab('all');
    setSelectedPositionId(null);
    setStaffingMode(service.isExpoDay || (service.positions && service.positions.length > 0) ? 'position' : 'general');

    // Initialize position assignments
    const assignments = new Map<string, string[]>();
    if (service.positions) {
      service.positions.forEach(pos => {
        assignments.set(pos.positionId, []);
      });
    }
    setPositionAssignments(assignments);
  };

  // Toggle staff selection (general mode)
  const toggleStaffSelection = (staffId: string) => {
    if (staffingMode === 'position' && selectedPositionId) {
      // Add to position assignment
      setPositionAssignments(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(selectedPositionId) || [];
        if (current.includes(staffId)) {
          newMap.set(selectedPositionId, current.filter(id => id !== staffId));
        } else {
          newMap.set(selectedPositionId, [...current, staffId]);
        }
        return newMap;
      });
      // Also add to general selection
      if (!selectedStaffIds.includes(staffId)) {
        setSelectedStaffIds(prev => [...prev, staffId]);
      }
    } else {
      setSelectedStaffIds(prev =>
        prev.includes(staffId)
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    }
  };

  // Remove staff from selection
  const removeStaff = (staffId: string) => {
    setSelectedStaffIds(prev => prev.filter(id => id !== staffId));
    // Remove from all position assignments
    setPositionAssignments(prev => {
      const newMap = new Map(prev);
      newMap.forEach((staffIds, posId) => {
        newMap.set(posId, staffIds.filter(id => id !== staffId));
      });
      return newMap;
    });
  };

  // Save staff assignments
  const handleSaveStaff = () => {
    if (!selectedService) return;

    const currentStaffIds = selectedService.staffIds || selectedService.staffs?.map(s => s.id) || [];
    const staffIdsToAdd = selectedStaffIds.filter(id => !currentStaffIds.includes(id));
    const staffIdsToDelete = currentStaffIds.filter(id => !selectedStaffIds.includes(id));

    updateServiceTicket(
      {
        id: selectedService.id,
        staffIds: staffIdsToAdd.length > 0 ? staffIdsToAdd : undefined,
        staffIdsToDelete: staffIdsToDelete.length > 0 ? staffIdsToDelete : undefined,
      },
      {
        onSuccess: () => {
          success({
            message: "Staff Updated",
            description: "Staff assignments have been saved successfully.",
          });
          setSelectedService(null);
        },
        onError: (err) => {
          error({
            message: "Failed to update staff",
            description: err.message,
          });
        },
      }
    );
  };

  // Approve and assign interested staff member
  const handleApproveStaff = (staffId: string, staffName: string) => {
    if (!selectedService) return;

    // Check if already assigned
    if (selectedStaffIds.includes(staffId)) {
      success({
        message: "Already Assigned",
        description: `${staffName} is already assigned to this service.`,
      });
      return;
    }

    // Add to selected staff
    setSelectedStaffIds(prev => [...prev, staffId]);

    // Immediately save the assignment
    const currentStaffIds = selectedService.staffIds || selectedService.staffs?.map(s => s.id) || [];

    updateServiceTicket(
      {
        id: selectedService.id,
        staffIds: [staffId],
      },
      {
        onSuccess: () => {
          success({
            message: "Staff Approved",
            description: `${staffName} has been assigned to this service.`,
          });
          refetchResponses();
        },
        onError: (err) => {
          // Remove from selected if save failed
          setSelectedStaffIds(prev => prev.filter(id => id !== staffId));
          error({
            message: "Failed to assign staff",
            description: err.message,
          });
        },
      }
    );
  };

  // Get staff name by ID
  const getStaffById = (staffId: string): TStaff | undefined => {
    return staffData?.items?.find(s => s.id === staffId);
  };

  // Get availability badge color
  const getAvailabilityColor = (avail: StaffWithAvailability) => {
    if (!avail.hasAvailabilityRecord) return 'default';
    if (avail.availability === StaffAvailability.UNAVAILABLE) return 'red';
    if (avail.isScheduledToday) return 'orange';
    if (avail.availability === StaffAvailability.PART_TIME) return 'gold';
    return 'green';
  };

  // Get availability label
  const getAvailabilityLabel = (avail: StaffWithAvailability) => {
    if (!avail.hasAvailabilityRecord) return 'Pending';
    if (avail.availability === StaffAvailability.UNAVAILABLE) return 'Unavailable';
    if (avail.isScheduledToday) return `Scheduled (${avail.bookedHours}h)`;
    if (avail.availability === StaffAvailability.PART_TIME) return 'Part-Time';
    return 'Available';
  };

  // Check if staff can be assigned
  const canAssignStaff = (avail: StaffWithAvailability) => {
    if (!avail.hasAvailabilityRecord) return false;
    if (avail.availability === StaffAvailability.UNAVAILABLE) return false;
    return true;
  };

  // Open Bat Signal Modal with preview
  const handleOpenBatSignalModal = async (position: { id: string; name: string }) => {
    setBatSignalPosition(position);
    setBatSignalModalOpen(true);
    setLoadingPreview(true);
    setBatSignalPreview(null);
    setSelectedBatSignalStaff(new Set());

    try {
      const response = await baseService.get(`/communications/bat-signal/preview/${position.id}`);
      setBatSignalPreview(response.data);
      // Select all staff by default
      setSelectedBatSignalStaff(new Set(response.data.staff.map((s: any) => s.id)));
    } catch (err: any) {
      error({
        message: "Failed to load preview",
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Toggle staff selection for bat signal
  const toggleBatSignalStaff = (staffId: string) => {
    setSelectedBatSignalStaff(prev => {
      const newSet = new Set(prev);
      if (newSet.has(staffId)) {
        newSet.delete(staffId);
      } else {
        newSet.add(staffId);
      }
      return newSet;
    });
  };

  // Select/deselect all bat signal staff
  const toggleAllBatSignalStaff = () => {
    if (!batSignalPreview?.staff) return;
    if (selectedBatSignalStaff.size === batSignalPreview.staff.length) {
      setSelectedBatSignalStaff(new Set());
    } else {
      setSelectedBatSignalStaff(new Set(batSignalPreview.staff.map(s => s.id)));
    }
  };

  // Send Job Opportunity Notification
  const handleSendBatSignal = async () => {
    if (!batSignalPosition || !selectedService || selectedBatSignalStaff.size === 0) return;

    setSendingBatSignal(true);
    try {
      // Get expo position details if available
      const expoPosition = selectedService.positions?.find(p => p.positionId === batSignalPosition.id);
      const duration = expoPosition?.duration || selectedService.duration;

      const response = await baseService.post('/communications/bat-signal', {
        positionId: batSignalPosition.id,
        serviceTicketId: selectedService.id,
        serviceDate: selectedService.serviceDatetime || selectedService.startDatetime,
        serviceType: selectedService.serviceType,
        location: formatFullAddress(selectedService.location),
        ...(typeof duration === 'number' && duration > 0 ? { duration } : {}),
        clientName: selectedService.client?.companyName || selectedService.jobTicket?.client?.companyName,
        // New fields
        staffIds: Array.from(selectedBatSignalStaff),
        isExpoDay: selectedService.isExpoDay || false,
        dayLabel: selectedService.dayLabel || undefined,
        notes: selectedService.staffNotes || selectedService.scope || undefined,
      });

      success({
        message: "Notifications Sent",
        description: `Job opportunity emails sent to ${response.data.emailsSent} staff.`,
      });
      setBatSignalModalOpen(false);
      setBatSignalPosition(null);
      setBatSignalPreview(null);
      setSelectedBatSignalStaff(new Set());
    } catch (err: any) {
      error({
        message: "Failed to send notifications",
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setSendingBatSignal(false);
    }
  };

  // Get staff count for a position
  const getPositionStaffCount = (positionId: string) => {
    return positionAssignments.get(positionId)?.length || 0;
  };

  // Table columns
  const columns: ColumnsType<TServiceTicket> = [
    {
      title: "Serial #",
      dataIndex: "serial",
      key: "serial",
      width: 120,
      render: (serial: string, record) => (
        <div>
          <span className="font-medium text-blue-600">{serial}</span>
          {record.isExpoDay && (
            <Tag color="purple" className="ml-2 text-xs">EXPO</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 150,
      render: (type: string, record) => (
        <div>
          <Tag color="blue">{capitalize(type.replace(/_/g, ' '))}</Tag>
          {record.dayLabel && (
            <div className="text-xs text-gray-500 mt-1">{record.dayLabel}</div>
          )}
        </div>
      ),
    },
    {
      title: "Date & Time",
      key: "datetime",
      width: 180,
      render: (_, record) => {
        const date = record.serviceDatetime || record.startDatetime;
        return (
          <div>
            <div className="font-medium">
              {date ? dayjs(date).format('ddd, MMM D, YYYY') : 'N/A'}
            </div>
            <div className="text-xs text-gray-500">
              {record.startDatetime
                ? dayjs(record.startDatetime).format('h:mm A')
                : 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: "Duration",
      key: "duration",
      width: 80,
      render: (_, record) => {
        let duration = record.duration;
        if (!duration && record.startDatetime && record.endDatetime) {
          const diffMs = dayjs(record.endDatetime).diff(dayjs(record.startDatetime), 'minute');
          duration = Math.round((diffMs / 60) * 10) / 10;
        }
        return (
          <div className="flex items-center gap-1 text-sm">
            <Clock size={12} className="text-gray-400" />
            <span>{duration ? `${duration}h` : '—'}</span>
          </div>
        );
      },
    },
    {
      title: "Client",
      key: "client",
      width: 150,
      render: (_, record) => (
        <span className="truncate">
          {record.client?.companyName || record.jobTicket?.client?.companyName || "—"}
        </span>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 200,
      render: (_, record) => (
        <Tooltip title={formatFullAddress(record?.location)}>
          <span className="truncate block max-w-[180px]">
            {formatFullAddress(record?.location) || "—"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Assigned Staff",
      key: "staff",
      render: (_, record) => {
        const staffList = record.staffs || [];
        const positionCount = record.positions?.reduce((sum, p) => sum + p.quantity, 0) || 0;

        if (staffList.length === 0) {
          return (
            <div>
              <span className="text-gray-400 italic text-sm">No staff assigned</span>
              {positionCount > 0 && (
                <div className="text-xs text-orange-500 mt-1">
                  {positionCount} positions needed
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            <div className="flex flex-wrap gap-1">
              {staffList.slice(0, 3).map((staff) => (
                <Tag key={staff.id} color="green" className="m-0">
                  {formatStaffName(staff)}
                </Tag>
              ))}
              {staffList.length > 3 && (
                <Tag color="default">+{staffList.length - 3}</Tag>
              )}
            </div>
            {positionCount > 0 && staffList.length < positionCount && (
              <div className="text-xs text-orange-500 mt-1">
                {staffList.length}/{positionCount} filled
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<ChevronRight size={16} />}
          onClick={() => handleSelectService(record)}
          className="text-gray-400 hover:text-blue-600"
        />
      ),
    },
  ];

  const tabItems = [
    {
      key: 'all',
      label: (
        <span className="flex items-center gap-2">
          <Users size={14} />
          All ({availabilityCounts.all})
        </span>
      ),
    },
    {
      key: 'available',
      label: (
        <span className="flex items-center gap-2">
          <UserCheck size={14} className="text-green-500" />
          Available ({availabilityCounts.available})
        </span>
      ),
    },
    {
      key: 'scheduled',
      label: (
        <span className="flex items-center gap-2">
          <Calendar size={14} className="text-orange-500" />
          Scheduled ({availabilityCounts.scheduled})
        </span>
      ),
    },
    {
      key: 'pending',
      label: (
        <span className="flex items-center gap-2">
          <HelpCircle size={14} className="text-gray-400" />
          Pending ({availabilityCounts.pending})
        </span>
      ),
    },
    {
      key: 'unavailable',
      label: (
        <span className="flex items-center gap-2">
          <UserX size={14} className="text-red-500" />
          Unavailable ({availabilityCounts.unavailable})
        </span>
      ),
    },
  ];

  if (viewMode === 'daily-staff') {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <Segmented
            value={viewMode}
            onChange={(val) => setViewMode(val as ViewMode)}
            options={[
              {
                value: 'services',
                label: (
                  <span className="flex items-center gap-2 px-1">
                    <LayoutList size={14} />
                    Service View
                  </span>
                ),
              },
              {
                value: 'daily-staff',
                label: (
                  <span className="flex items-center gap-2 px-1">
                    <Columns3 size={14} />
                    Daily Staff View
                  </span>
                ),
              },
            ]}
          />
        </div>
        <DailyStaffView projectId={projectId} />
      </div>
    );
  }

  return (
    <div>
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <Segmented
          value={viewMode}
          onChange={(val) => setViewMode(val as ViewMode)}
          options={[
            {
              value: 'services',
              label: (
                <span className="flex items-center gap-2 px-1">
                  <LayoutList size={14} />
                  Service View
                </span>
              ),
            },
            {
              value: 'daily-staff',
              label: (
                <span className="flex items-center gap-2 px-1">
                  <Columns3 size={14} />
                  Daily Staff View
                </span>
              ),
            },
          ]}
        />
      </div>

    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
      {/* Left side: Service Table */}
      <div className={`${selectedService ? 'col-span-7' : 'col-span-12'} bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col transition-all`}>
        <div className="p-4 border-b font-medium bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-gray-500" />
            <span>Services & Staff Assignments ({projectServices.length})</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <Table
            columns={columns}
            dataSource={projectServices}
            rowKey="id"
            loading={bookingsLoading}
            pagination={false}
            size="small"
            scroll={{ y: 'calc(100vh - 320px)' }}
            onRow={(record) => ({
              onClick: () => handleSelectService(record),
              className: `cursor-pointer ${selectedService?.id === record.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`,
            })}
            locale={{
              emptyText: <Empty description="No services found for this project" />,
            }}
          />
        </div>
      </div>

      {/* Right side: Staff Assignment Panel */}
      {selectedService && (
        <div className="col-span-5 bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-lg">Assign Staff</h3>
              <Button
                type="text"
                size="small"
                icon={<X size={18} />}
                onClick={() => setSelectedService(null)}
              />
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium text-blue-600">{selectedService.serial}</span>
              {selectedService.isExpoDay && (
                <Tag color="purple" className="ml-2">EXPO</Tag>
              )}
              {selectedService.dayLabel && (
                <span className="ml-2 text-gray-500">({selectedService.dayLabel})</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Calendar size={12} />
              {dayjs(selectedService.serviceDatetime || selectedService.startDatetime).format('dddd, MMM D, YYYY')}
            </div>
          </div>

          {/* Expo Positions Section */}
          {isExpoService && expoPositions.length > 0 && (
            <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-purple-800">
                  Required Positions
                </div>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    type={staffingMode === 'position' ? 'primary' : 'default'}
                    onClick={() => setStaffingMode('position')}
                  >
                    By Position
                  </Button>
                  <Button
                    size="small"
                    type={staffingMode === 'general' ? 'primary' : 'default'}
                    onClick={() => {
                      setStaffingMode('general');
                      setSelectedPositionId(null);
                    }}
                  >
                    General
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {expoPositions.map((pos, idx) => {
                  const assignedCount = getPositionStaffCount(pos.positionId);
                  const isSelected = selectedPositionId === pos.positionId;
                  const isFilled = assignedCount >= pos.quantity;

                  return (
                    <div
                      key={pos.id || idx}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'bg-purple-100 border-purple-400 shadow-sm'
                          : isFilled
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          if (staffingMode === 'position') {
                            setSelectedPositionId(isSelected ? null : pos.positionId);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{pos.position?.name || 'Position'}</span>
                          {isFilled && (
                            <Tag color="success" className="m-0 text-xs">Filled</Tag>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {pos.startTime} • {pos.duration}h • ${pos.hourlyRate}/hr
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            count={`${assignedCount}/${pos.quantity}`}
                            style={{
                              backgroundColor: isFilled ? '#52c41a' : assignedCount > 0 ? '#faad14' : '#d9d9d9'
                            }}
                          />
                          {/* Show assigned staff names */}
                          {positionAssignments.get(pos.positionId)?.slice(0, 2).map(staffId => {
                            const staff = getStaffById(staffId);
                            return staff ? (
                              <Tag key={staffId} color="blue" className="m-0 text-xs">
                                {formatStaffName(staff)}
                              </Tag>
                            ) : null;
                          })}
                        </div>
                      </div>

                      {/* Job Opportunity Notification Button */}
                      <Tooltip title={`Send job alert to all ${pos.position?.name || 'Position'} staff`}>
                        <Button
                          type="text"
                          size="small"
                          icon={<Mail size={16} className="text-blue-500" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenBatSignalModal({
                              id: pos.positionId,
                              name: pos.position?.name || 'Position'
                            });
                          }}
                          className="hover:bg-blue-50"
                        />
                      </Tooltip>
                    </div>
                  );
                })}
              </div>

              {selectedPositionId && (
                <div className="mt-3 p-2 bg-purple-100 rounded-lg text-sm text-purple-800">
                  <AlertCircle size={14} className="inline mr-1" />
                  Click staff below to assign them to: <strong>{expoPositions.find(p => p.positionId === selectedPositionId)?.position?.name}</strong>
                </div>
              )}
            </div>
          )}

          {/* Currently Assigned Staff */}
          <div className="p-4 border-b">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Currently Assigned ({selectedStaffIds.length})
              {isExpoService && expoPositions.length > 0 && (
                <span className="text-gray-400 font-normal ml-2">
                  / {expoPositions.reduce((sum, p) => sum + p.quantity, 0)} needed
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 min-h-[40px]">
              {selectedStaffIds.length === 0 ? (
                <span className="text-gray-400 italic text-sm">No staff assigned</span>
              ) : (
                selectedStaffIds.map((staffId) => {
                  const staff = getStaffById(staffId);
                  return (
                    <div
                      key={staffId}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      <span>{staff ? formatStaffName(staff) : staffId.slice(-6)}</span>
                      <div className="flex gap-0.5">
                        {staff?.driver && (
                          <span className="bg-green-500 text-white px-1 rounded text-xs">D</span>
                        )}
                        {staff?.lead && (
                          <span className="bg-purple-500 text-white px-1 rounded text-xs">L</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeStaff(staffId)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Interested Staff - Staff who accepted bat signal */}
          {interestedStaff.length > 0 && (
            <div className="p-4 border-b bg-green-50">
              <div className="flex items-center gap-2 text-sm font-medium text-green-800 mb-2">
                <ThumbsUp size={16} />
                Interested Staff ({interestedStaff.length})
              </div>
              <div className="space-y-2">
                {interestedStaff.map((response) => {
                  const isAlreadyAssigned = selectedStaffIds.includes(response.staffId);
                  return (
                    <div
                      key={response.staffId}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border border-green-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Check size={16} className="text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{response.staffName}</div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Tag color="blue" className="m-0">{response.positionName}</Tag>
                            {response.respondedAt && (
                              <span>
                                Responded {dayjs(response.respondedAt).format("MMM D, h:mm A")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        {isAlreadyAssigned ? (
                          <Tag color="success" className="m-0">
                            <Check size={12} className="inline mr-1" />
                            Assigned
                          </Tag>
                        ) : (
                          <Button
                            type="primary"
                            size="small"
                            icon={<UserCheck size={14} />}
                            onClick={() => handleApproveStaff(response.staffId, response.staffName)}
                            loading={isUpdating}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Availability Tabs */}
          <div className="border-b">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as AvailabilityTab)}
              items={tabItems}
              size="small"
              className="px-4"
            />
          </div>

          {/* Search Staff */}
          <div className="p-4 border-b">
            <Input
              placeholder="Search staff by name..."
              prefix={<Search size={16} className="text-gray-400" />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </div>

          {/* Staff List */}
          <div className="flex-1 overflow-y-auto p-4">
            <Spin spinning={staffLoading || availabilityLoading}>
              {filteredStaff.length === 0 ? (
                <Empty description={selectedPositionId ? "No staff with this position found" : "No staff found"} />
              ) : (
                <div className="space-y-2">
                  {filteredStaff.map((staffAvail) => {
                    const { staff, availability, hasAvailabilityRecord, bookedHours, availableHours, note } = staffAvail;
                    const isSelected = selectedStaffIds.includes(staff.id);
                    const canAssign = canAssignStaff(staffAvail);
                    const isAssignedToPosition = selectedPositionId
                      ? positionAssignments.get(selectedPositionId)?.includes(staff.id)
                      : false;

                    return (
                      <div
                        key={staff.id}
                        className={`p-3 border rounded-lg transition-all ${
                          isAssignedToPosition
                            ? 'border-purple-500 bg-purple-50 cursor-pointer'
                            : isSelected
                            ? 'border-blue-500 bg-blue-50 cursor-pointer'
                            : !canAssign
                            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            : 'hover:bg-gray-50 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => canAssign && toggleStaffSelection(staff.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected || isAssignedToPosition}
                              disabled={!canAssign}
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{formatStaffName(staff)}</span>
                                <Tag
                                  color={getAvailabilityColor(staffAvail)}
                                  className="m-0 text-xs"
                                >
                                  {getAvailabilityLabel(staffAvail)}
                                </Tag>
                              </div>
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {staff.driver && (
                                  <Tag color="green" className="m-0 text-xs">Driver</Tag>
                                )}
                                {staff.lead && (
                                  <Tag color="purple" className="m-0 text-xs">Lead</Tag>
                                )}
                                {staff.staffRole && (
                                  <Tag color="blue" className="m-0 text-xs">{staff.staffRole.name}</Tag>
                                )}
                                {staff.positions?.slice(0, 2).map(pos => (
                                  <Tag
                                    key={pos.id}
                                    color={selectedPositionId === pos.id ? 'purple' : 'default'}
                                    className="m-0 text-xs"
                                  >
                                    {pos.name}
                                  </Tag>
                                ))}
                                {staff.positions && staff.positions.length > 2 && (
                                  <Tag color="default" className="m-0 text-xs">
                                    +{staff.positions.length - 2}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {hasAvailabilityRecord && availability !== StaffAvailability.UNAVAILABLE && (
                              <div className="text-xs text-gray-500">
                                <div className="flex items-center gap-1 justify-end">
                                  <Clock size={10} />
                                  {availableHours}h available
                                </div>
                                {bookedHours > 0 && (
                                  <div className="text-orange-500">
                                    {bookedHours}h booked
                                  </div>
                                )}
                              </div>
                            )}
                            {!hasAvailabilityRecord && (
                              <div className="text-xs text-gray-400">
                                No availability set
                              </div>
                            )}
                            {!isSelected && canAssign && (
                              <Button
                                type="text"
                                size="small"
                                icon={<Plus size={16} />}
                                className="text-blue-600 mt-1"
                              />
                            )}
                          </div>
                        </div>
                        {note && (
                          <div className="text-xs text-gray-500 italic mt-2 pt-2 border-t">
                            {note}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Spin>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <Button onClick={() => setSelectedService(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveStaff}
              loading={isUpdating}
            >
              Save Assignments
            </Button>
          </div>
        </div>
      )}

      {/* Job Opportunity Notification Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Mail className="text-blue-500" size={20} />
            <span>Send Job Opportunity</span>
          </div>
        }
        open={batSignalModalOpen}
        onCancel={() => {
          setBatSignalModalOpen(false);
          setBatSignalPosition(null);
          setBatSignalPreview(null);
        }}
        width={520}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setBatSignalModalOpen(false);
              setBatSignalPosition(null);
              setBatSignalPreview(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<Mail size={16} />}
            loading={sendingBatSignal}
            onClick={handleSendBatSignal}
            disabled={loadingPreview || selectedBatSignalStaff.size === 0}
            style={{ backgroundColor: '#3b82f6' }}
          >
            Send to {selectedBatSignalStaff.size} Staff
          </Button>,
        ]}
      >
        <div className="py-4">
          {/* Job Details */}
          {selectedService && (
            <div className="bg-gray-900 text-white rounded-lg p-4 mb-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="text-blue-400 font-mono text-sm">{selectedService.serial}</span>
                  <h4 className="font-medium text-lg">
                    {capitalize(selectedService.serviceType.replace(/_/g, ' '))}
                  </h4>
                  {selectedService.isExpoDay && selectedService.dayLabel && (
                    <div className="flex items-center gap-2 mt-1">
                      <Tag color="purple" className="m-0 text-xs">EXPO</Tag>
                      <span className="text-gray-400 text-sm">{selectedService.dayLabel}</span>
                    </div>
                  )}
                </div>
                <Tag color="blue" className="m-0">{batSignalPosition?.name}</Tag>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Date</span>
                  <div className="text-white">
                    {dayjs(selectedService.serviceDatetime || selectedService.startDatetime).format('ddd, MMM D, YYYY')}
                  </div>
                </div>
                <div>
                  <span className="text-gray-400">Time</span>
                  <div className="text-white">
                    {dayjs(selectedService.startDatetime).format('h:mm A')}
                    {selectedService.duration && ` (${selectedService.duration}h)`}
                  </div>
                </div>
                {(selectedService.client?.companyName || selectedService.jobTicket?.client?.companyName) && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Client</span>
                    <div className="text-white">
                      {selectedService.client?.companyName || selectedService.jobTicket?.client?.companyName}
                    </div>
                  </div>
                )}
                {(selectedService.staffNotes || selectedService.scope) && (
                  <div className="col-span-2 mt-1">
                    <span className="text-gray-400">Notes</span>
                    <div className="text-gray-300 text-xs mt-1">
                      {selectedService.staffNotes || selectedService.scope}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Staff Preview */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                {batSignalPreview && batSignalPreview.staff.length > 0 && (
                  <Checkbox
                    checked={selectedBatSignalStaff.size === batSignalPreview.staff.length}
                    indeterminate={selectedBatSignalStaff.size > 0 && selectedBatSignalStaff.size < batSignalPreview.staff.length}
                    onChange={toggleAllBatSignalStaff}
                  />
                )}
                <span className="font-medium text-gray-700">
                  Select recipients
                </span>
              </div>
              {batSignalPreview && (
                <span className="text-sm text-gray-500">
                  {selectedBatSignalStaff.size} of {batSignalPreview.staffCount} selected
                </span>
              )}
            </div>

            <div className="max-h-[240px] overflow-y-auto">
              {loadingPreview ? (
                <div className="p-8 text-center">
                  <Spin />
                  <p className="text-gray-500 text-sm mt-2">Loading staff...</p>
                </div>
              ) : batSignalPreview?.staff.length === 0 ? (
                <div className="p-8 text-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span className="text-gray-500">
                        No staff found with this position
                      </span>
                    }
                  />
                </div>
              ) : (
                <div className="divide-y">
                  {batSignalPreview?.staff.map((staffMember) => {
                    const isSelected = selectedBatSignalStaff.has(staffMember.id);
                    return (
                      <div
                        key={staffMember.id}
                        className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleBatSignalStaff(staffMember.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox checked={isSelected} />
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                            isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {staffMember.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{staffMember.name}</div>
                            <div className="text-xs text-gray-500">{staffMember.email}</div>
                          </div>
                        </div>
                        {staffMember.role && (
                          <Tag color="default" className="m-0 text-xs">{staffMember.role}</Tag>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Info Note */}
          <div className="mt-4 text-xs text-gray-500 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Each staff member will receive an email with accept/decline buttons.
              Responses expire after 48 hours.
            </span>
          </div>
        </div>
      </Modal>
    </div>
    </div>
  );
};
